import { buildBrowserLikePreviewSrcdoc, htmlSourceToSafePreviewHtml } from "./htmlPreview.js";
import { bytesToHex64 } from "./nullaCodec.js";

/** `<input type="file" accept>` for HTML fragments or full documents. */
export const MAX_BROWSER_HTML_FILE_ACCEPT = ".html,.htm,text/html,application/xhtml+xml";

/** Cap for a single embedded HTML file (UTF-8 text before sanitize). */
export const MAX_MAX_HTML_EMBED_BYTES = 2 * 1024 * 1024;

/** Stored in markdown as `[label](<npp-max-html:1;<hex64>>)`; preview mounts an iframe from decoded UTF-8. */
export const MAX_MAX_HTML_HEX_URI_PREFIX = "npp-max-html:1;";

export async function readHtmlFileAsUtf8Text(file: File): Promise<string> {
  if (file.size > MAX_MAX_HTML_EMBED_BYTES) {
    const mb = Math.round(MAX_MAX_HTML_EMBED_BYTES / (1024 * 1024));
    return Promise.reject(new Error(`HTML file is larger than ${mb} MB.`));
  }
  return file.text();
}

/**
 * Sanitize HTML and normalize to an inner fragment suitable for embedding under Max preview.
 * Full documents become `document.body.innerHTML` after sanitize + parse.
 */
export function sanitizeAndExtractHtmlForMaxEmbed(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  let inner = htmlSourceToSafePreviewHtml(trimmed).trim();
  if (!inner) return "";
  const head = inner.slice(0, 120).toLowerCase();
  if (head.includes("<html") || head.includes("<!doctype")) {
    try {
      const doc = new DOMParser().parseFromString(inner, "text/html");
      inner = doc.body?.innerHTML?.trim() ?? inner;
    } catch {
      /* keep sanitized string */
    }
  }
  return inner;
}

/** Block wrapper so KaTeX auto-render can skip the subtree via `ignoredClasses`. */
export function buildMaxHtmlEmbedBlock(sanitizedInnerHtml: string): string {
  return `\n\n<div class="npp-max-html-embed">${sanitizedInnerHtml}</div>\n\n`;
}

/**
 * Markdown line carrying a full sanitized HTML document as UTF-8 → hex64 (see {@link MAX_MAX_HTML_HEX_URI_PREFIX}).
 * Angle brackets keep very long URLs valid in CommonMark.
 */
export function buildMaxHtmlHexMarkdownLine(displayLabel: string, srcdocUtf8: string): string {
  const hex = bytesToHex64(new TextEncoder().encode(srcdocUtf8));
  const safeLabel =
    displayLabel.replace(/[\[\]\r\n()]/g, " ").trim().slice(0, 120) || "HTML";
  return `\n\n[${safeLabel}](<${MAX_MAX_HTML_HEX_URI_PREFIX}${hex}>)\n\n`;
}

/**
 * Build the Insert HTML markdown blob from raw file bytes, or `null` if nothing survives sanitization.
 */
export function buildMaxHtmlHexEmbedMarkdownFromRawHtml(raw: string, fileNameForLabel: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const safe = htmlSourceToSafePreviewHtml(trimmed);
  if (!safe.trim()) return null;
  const srcdoc = buildBrowserLikePreviewSrcdoc(raw);
  const base = fileNameForLabel.replace(/\.[^.]+$/i, "").trim() || "HTML";
  return buildMaxHtmlHexMarkdownLine(base, srcdoc);
}
